import React, { useEffect, useMemo, useState, useRef } from 'react'; // Added useState import
import { array, func, string, bool } from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  Box,
  Typography,
  LinearProgress,
  Alert,
} from 'components/mui';
import { useContactList } from 'containers/pages/contacts/hooks';
import { useNotificationProvider } from 'hooks';
import { AddIcon, RemoveIcon } from 'components/icons';
import ContactFormUnified from 'containers/pages/contacts/components/ContactFormUnified/ContactFormUnified';
import ContactDetailPanel from 'containers/pages/contacts/components/contactDetail/ContactDetailPanel';
import useContactDetail from 'containers/pages/contacts/hooks/useContactDetail';
import CompanyFilterBtn from '../companyFilterBtn/CompanyFilterBtn';
import { getComparator, stableSort } from 'libs/sortList';
import styles from './ContactList.module.css';

/** Infinite scroll tuning */
const INITIAL_VISIBLE_COUNT = 50;
const LOAD_MORE_COUNT = 50;
const SCROLL_THRESHOLD = 100; // px from bottom

function ContactList(props) {
  const { state, handler } = useContactList();
  const { notifyError } = useNotificationProvider();
  const processedContactIdRef = useRef(null);
  const { state: detailState, handler: detailHandler } = useContactDetail();

  // Filter to show only the highlighted contact if contactId param exists
  const dataSource = props.highlightContactId
    ? props.searchedContacts.filter(
        contact => String(contact.id) === String(props.highlightContactId)
      )
    : props.searchedContacts;

  // Get the highlighted contact for displaying name in banner
  const highlightedContact = props.highlightContactId
    ? props.searchedContacts.find(
        contact => String(contact.id) === String(props.highlightContactId)
      )
    : null;

  // sorting
  const sortedContacts = useMemo(
    () => stableSort(dataSource, getComparator(state.order, state.orderBy)),
    [dataSource, state.order, state.orderBy]
  );

  // progressive reveal - Fixed useState usage
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [sortedContacts, state.order, state.orderBy]);

  const handleScroll = React.useCallback(
    (e) => {
      const target = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const nearBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;

      if (nearBottom && !isLoadingMore && visibleCount < sortedContacts.length) {
        setIsLoadingMore(true);
        setTimeout(() => {
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_COUNT, sortedContacts.length));
          setIsLoadingMore(false);
        }, 100);
      }
    },
    [isLoadingMore, visibleCount, sortedContacts.length]
  );

  const visibleRows = React.useMemo(
    () => sortedContacts.slice(0, visibleCount),
    [sortedContacts, visibleCount]
  );

  // Helper function to scroll to contact and open detail panel
  const scrollToContact = React.useCallback((contactId, contactIndex) => {
    // Find the row element using data attribute
    const rowElement = document.querySelector(`[data-contact-id="${contactId}"]`);

    if (!rowElement) {
      // Row not yet in DOM, try again after a short delay
      setTimeout(() => scrollToContact(contactId, contactIndex), 50);
      return;
    }

    // Scroll to the row - position at top so expanded content is visible
    rowElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest'
    });

    // Open the detail panel for this contact
    detailHandler.openPanel(contactId);
  }, [detailHandler]);

  // Effect to handle scrolling to highlighted contact
  useEffect(() => {
    const highlightContactId = props.highlightContactId;
    
    // Only process if we have a contactId and haven't processed it yet
    if (!highlightContactId || processedContactIdRef.current === highlightContactId) {
      return;
    }
    
    // Wait for contacts to be loaded - check the full searchedContacts, not filtered dataSource
    if (props.searchedContacts.length === 0) {
      return;
    }
    
    // Find contact in the full searchedContacts list (before filtering)
    const contactExists = props.searchedContacts.some(
      contact => String(contact.id) === String(highlightContactId)
    );
    
    if (!contactExists) {
      // Contact not found - show error and exit focused view
      notifyError('Contact not found');
      processedContactIdRef.current = highlightContactId; // Mark as processed to avoid repeated errors
      // Exit focused view to show all contacts
      if (props.onExitFocusedView) {
        props.onExitFocusedView();
      }
      return;
    }
    
    // Contact exists - find its index in sorted list for scrolling
    const contactIndex = sortedContacts.findIndex(
      contact => String(contact.id) === String(highlightContactId)
    );
    
    // Mark as processing
    processedContactIdRef.current = highlightContactId;
    
    // If contact is in sorted list, scroll to it
    if (contactIndex !== -1) {
      // Expand visibleCount if contact is beyond visible rows
      if (contactIndex >= visibleCount) {
        setVisibleCount(contactIndex + 1);
        // Wait for DOM update before scrolling
        setTimeout(() => {
          scrollToContact(highlightContactId, contactIndex);
        }, 100);
      } else {
        // Contact already visible, scroll immediately
        setTimeout(() => {
          scrollToContact(highlightContactId, contactIndex);
        }, 50);
      }
    }
  }, [props.highlightContactId, props.searchedContacts, sortedContacts, visibleCount, notifyError, scrollToContact, props.onExitFocusedView]);

  const totalHeaderCols = state.headTableCell?.length ?? 6;
  const hasExpandHeader = state.headTableCell?.some(
    (c) =>
      ['expand', 'actions', 'toggle'].includes(c.id) ||
      ['expand', 'actions', 'toggle'].includes(c.property)
  );
  const detailsColSpan = hasExpandHeader ? totalHeaderCols : totalHeaderCols + 1;

  const hideOnMobileProps = new Set(['phoneWork', 'phoneMobile', 'emailWork']);

  return (
    <Paper elevation={1}>
      {/* Alert banner for focused view */}
      {props.highlightContactId && highlightedContact && (
        <Alert 
          severity="warning" 
          onClick={props.onExitFocusedView}
          sx={{ 
            cursor: 'pointer',
            borderRadius: 0,
            backgroundColor: '#fff3cd',
            color: '#856404',
            '&:hover': {
              backgroundColor: '#ffe69c',
            }
          }}
        >
          Viewing single contact: {highlightedContact.firstName} {highlightedContact.lastName}. Click to view all contacts.
        </Alert>
      )}
      
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', display:'flex', justifyContent:'space-between' }}>
        <Typography variant="subtitle2" color="text.secondary">
          {props.isCompanyFiltering
            ? 'Loading filtered contacts…'
            : `Showing ${visibleRows.length} of ${sortedContacts.length} contacts`}
        </Typography>
        <CompanyFilterBtn
          value={props.selectedCompany}
          onChange={props.onCompanyChange}
        />
      </Box>

      <TableContainer
        onScroll={handleScroll}
        sx={{
          maxHeight: { xs: '62vh', md: '70vh' },
          overflowX: 'auto',
          '@media (min-width:430px) and (max-width:899px)': {
            maxHeight: '66vh',
          },
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              {state.headTableCell.map((cell) => {
                const isActive = state.orderBy === cell.property;
                const direction = isActive ? state.order : 'asc';
                const hideOnMobile = hideOnMobileProps.has(cell.property);

                return (
                  <TableCell
                    key={cell.id ?? cell.property}
                    sortDirection={isActive ? state.order : false}
                    sx={hideOnMobile ? { display: { xs: 'none', sm: 'table-cell' } } : undefined}
                  >
                    <TableSortLabel
                      active={isActive}
                      direction={direction}
                      onClick={handler.onSort(cell.property)}
                    >
                      {cell.label}
                      {isActive ? (
                        <span className={styles.visuallyHidden}>
                          {state.order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.map((contact) => {
              const isOpen = state.selectedIds.includes(contact.id);
              return (
                <React.Fragment key={contact.id}>
                  
                  {/* SUMMARY ROW */}
                  <TableRow
                    hover
                    onClick={() => detailHandler.openPanel(contact.id)}
                    sx={{ cursor: 'pointer' }}
                    data-contact-id={contact.id}
                  >
                    <TableCell>
                      <IconButton
                        aria-label={isOpen ? 'Collapse' : 'Expand'}
                        aria-expanded={isOpen}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handler.onForm(contact.id);
                        }}
                      >
                        {isOpen ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </TableCell>

                    <TableCell>{contact.firstName}</TableCell>
                    <TableCell>{contact.lastName}</TableCell>

                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {contact.phoneWork}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {contact.phoneMobile}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      {contact.emailWork}
                    </TableCell>
                  </TableRow>

                  {/* DETAILS ROW */}
                  <TableRow>
                    <TableCell sx={{ p: 0, overflowX: 'hidden', maxWidth: '100%' }} colSpan={detailsColSpan}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <div style={{ padding: 16 }}>
                          <ContactFormUnified
                            mode="edit"
                            id={contact.id}
                            companies={props.companies}
                            contactTypes={props.contactTypes}
                            languageTypes={props.languageTypes}
                            onFetchContacts={props.onContacts}
                          />
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}

            {isLoadingMore && (
              <TableRow>
                <TableCell colSpan={detailsColSpan}>
                  <Box sx={{ width: '100%', py: 1 }}>
                    <LinearProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 1, textAlign: 'center' }}>
        {!isLoadingMore && visibleRows.length >= sortedContacts.length && (
          <Typography variant="caption" color="text.secondary">
            End of list
          </Typography>
        )}
      </Box>

      <ContactDetailPanel
        open={detailState.open}
        onClose={detailHandler.closePanel}
        contactData={detailState.contactData}
        loading={detailState.loading}
        error={detailState.error}
        onRefetch={detailHandler.refetch}
        companies={props.companies}
        contactTypes={props.contactTypes}
        languageTypes={props.languageTypes}
        onFetchContacts={props.onContacts}
      />
    </Paper>
  );
}

ContactList.propTypes = {
  companies: array,
  contactTypes: array,
  languageTypes: array,
  searchedContacts: array,
  searchInput: string,
  selectedCompany: string,
  isCompanyFiltering: bool,
  onContacts: func,
  onCompanyChange: func,
  highlightContactId: string,
  onExitFocusedView: func,
};

export default ContactList;